/* jshint -W024 */
/* eslint-disable no-console */
import Vue from 'vue';
import VueRouter from 'vue-router';
import i18n from '@/ui/i18n';

import ClientRoutes from '@/ui/router/internal/client-routes';
import UserRoutes from '@/ui/router/internal/user-routes';
import LoginRoutes from '@/ui/router/public/login-routes';
import BackOfficeRoutes from '@/ui/router/internal/back-office-routes';
import DashboardRoutes from '@/ui/router/internal/dashboard-routes';
import InvestorRoutes from '@/ui/router/internal/investor-routes';
import Roles from '@/domain/rolesEnum';
import { authenticationService } from '@/application/services/api-clients/login-service';
import { requestFunnel } from '@/application/api';
import store from '@/ui/store';

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject);
  return originalPush.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated') {
      console.log(err); // TODO: revisar los casos cuando estado en la página x se llama a la misma página x
    }
  });
};

Vue.use(VueRouter);
const childrenOfMain = [
  ...DashboardRoutes,
  ...BackOfficeRoutes,
  ...ClientRoutes,
  ...InvestorRoutes,
  ...UserRoutes
];
const routes = [
  {
    path: '/',
    name: 'main',
    component: () => import('@/ui/layout/internal/Internal.vue'),
    meta: {
      auth: true,
      roles: []
    },
    redirect: () => {
      const { currentUser } = store.state;

      if (currentUser === null) { return '/login'; }

      if (
        currentUser.user.checkIn && !currentUser.user.checkIn.is && currentUser.user.checkIn.required
      ) { return 'check-in'; }

      const { path } = childrenOfMain.find(child => {
        const isUserAdmin = currentUser.user.rol === Roles.ADMIN;
        const userIncludesRoles = (child.meta.roles || []).includes(currentUser.user.rol);
        const userIncludesAllowedRoutes = (currentUser.user.allowedFrontRoutesNames || []).includes(child.name);
        const userIncludesAllowedSidebarUserFeatures = (currentUser.user.allowedUserFeatures || [])
          .filter(name => name.split('.')[1] === 'sidebar')
          .map(name => name.split('.')[2])
          .includes(child.name);

        return isUserAdmin || userIncludesRoles || userIncludesAllowedRoutes || userIncludesAllowedSidebarUserFeatures;
      });

      return path;
    },
    children: childrenOfMain
  },
  {
    path: '/login',
    component: () => import('@/ui/layout/public/login.vue'),
    children: [
      ...LoginRoutes
    ]
  },
  {
    path: '/document',
    component: () => import('@/ui/views/public/DocumentForm.vue')
  },
  {
    path: '/re-token',
    name: 're-token',
    component: () => import('@/ui/views/public/ReTokenForm.vue')
  },
  {
    path: '/financial-portability',
    component: () => import('@/ui/views/public/FinancialPortabilityForm.vue')
  },
  {
    path: '*',
    redirect: '/'
  }
];

const router = new VueRouter({
  base: i18n.locale || 'cl',
  routes,
  mode: 'history'
});

router.beforeEach((to, from, next) => {
  const authorize = to.matched.some(m => m.meta.auth);
  if (authorize) {
    const { currentUser } = store.state;
    if (currentUser === null || !currentUser.date) {
      authenticationService.logout();
      next({ name: 'login', query: { returnUrl: to.name } });
      return;
    }

    const { checkIn } = currentUser.user;
    const isNameExcluded = !['welcome', 'terms', 'confirmed', 'support'].includes(to.name);
    if (
      checkIn && !checkIn.is && checkIn.required && isNameExcluded
    ) {
      next({ name: 'check-in', replace: true });
      return;
    }

    const currentDate = new Date();
    const days = Math.abs(currentDate - new Date(currentUser.date)) / (1000 * 60 * 60 * 24);
    if (days > 7) {
      authenticationService.logout();
      next({ name: 'login', query: { returnUrl: to.name } });
      return;
    }

    const roles = to.meta.roles || [];
    const hasAllowedFrontRoutesNames = (currentUser.user.allowedFrontRoutesNames || []).includes(to.name);
    const hasAllowedSidebarUserFeatures = (currentUser.user.allowedUserFeatures || [])
      .filter(name => name.split('.')[1] === 'sidebar')
      .map(name => name.split('.')[2])
      .includes(to.name);
    if (
      currentUser.user.rol !== Roles.ADMIN && roles.length && !roles.includes(currentUser.user.rol) && !hasAllowedFrontRoutesNames && !hasAllowedSidebarUserFeatures
    ) {
      requestFunnel.post('blank-page', { currentUser, to });
      next({ path: 'main', replace: true });
      return;
    }
  }
  next();
});

export default router;
